import { useMemo, useState, useEffect } from "react";
import { CardData } from "./GridLayout";
import { FilterValue } from "@/components/cards/widgets/TableFilter";
import { getTagById } from "@/lib/stores/tagsStore";

export interface UseCardFiltersProps {
  cards: CardData[];
  requiredTags?: string[];
  selectedFolderId?: string | null;
  filterConfig?: any[];
  itemType?: string; // Used to persist filters per item type
}

export interface UseCardFiltersReturn {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  activeFilters: FilterValue[];
  setActiveFilters: (filters: FilterValue[]) => void;
  shadowMode: "none" | "black" | "status" | "template" | "folder";
  setShadowMode: (mode: "none" | "black" | "status" | "template" | "folder") => void;
  filteredCards: CardData[];
  allTags: string[];
}

export function useCardFilters({
  cards,
  requiredTags = [],
  selectedFolderId,
  filterConfig,
  itemType,
}: UseCardFiltersProps): UseCardFiltersReturn {
  // Persist filters per itemType
  const filterStorageKey = itemType ? `locanest-filters-${itemType}` : null;
  const shadowModeStorageKey = itemType ? `locanest-shadow-mode-${itemType}` : null;
  const searchTermStorageKey = itemType ? `locanest-search-${itemType}` : null;
  
  const [searchTerm, setSearchTermInternal] = useState(() => {
    if (!searchTermStorageKey) return "";
    try {
      return localStorage.getItem(searchTermStorageKey) || "";
    } catch {
      return "";
    }
  });
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>(() => {
    if (!filterStorageKey) return [];
    try {
      const saved = localStorage.getItem(filterStorageKey);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [shadowMode, setShadowMode] = useState<"none" | "black" | "status" | "template" | "folder">(() => {
    if (!shadowModeStorageKey) return "folder";
    const saved = localStorage.getItem(shadowModeStorageKey);
    if (saved === "none" || saved === "black" || saved === "status" || saved === "template" || saved === "folder") {
      return saved;
    }
    return "folder";
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (filterStorageKey) {
      localStorage.setItem(filterStorageKey, JSON.stringify(activeFilters));
    }
  }, [filterStorageKey, activeFilters]);

  // Save shadowMode to localStorage whenever it changes
  useEffect(() => {
    if (shadowModeStorageKey) {
      localStorage.setItem(shadowModeStorageKey, shadowMode);
    }
  }, [shadowModeStorageKey, shadowMode]);

  // Save searchTerm to localStorage whenever it changes
  useEffect(() => {
    if (searchTermStorageKey) {
      localStorage.setItem(searchTermStorageKey, searchTerm);
    }
  }, [searchTermStorageKey, searchTerm]);

  // Wrapper for setSearchTerm to update internal state
  const setSearchTerm = (term: string) => {
    setSearchTermInternal(term);
  };

  // Get all unique tags from cards
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    cards.forEach((card) => {
      if (card.tags && Array.isArray(card.tags)) {
        card.tags.forEach((tag) => tags.add(tag));
      }
    });
    return Array.from(tags).sort();
  }, [cards]);

  // Filter cards based on all criteria
  const filteredCards = useMemo(() => {
    return cards.filter((card) => {
      // Hard tag filter (required tags)
      if (requiredTags.length > 0) {
        if (!card.tags || !card.tags.some((tag) => requiredTags.includes(tag))) {
          return false;
        }
      }

      // Folder filter
      if (selectedFolderId !== undefined) {
        const cardFolder = (card as any).folder;
        
        if (selectedFolderId === null || selectedFolderId === 'root' || selectedFolderId === '') {
          if (cardFolder && cardFolder !== null && cardFolder !== '') {
            return false;
          }
        } else {
          if (cardFolder !== selectedFolderId) {
            return false;
          }
        }
      }

      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        searchLower === "" ||
        card.title.toLowerCase().includes(searchLower) ||
        card.creator.toLowerCase().includes(searchLower) ||
        card.description?.toLowerCase().includes(searchLower) ||
        card.tags?.some((tag) => tag.toLowerCase().includes(searchLower));

      if (!matchesSearch) return false;

      // Apply unified filters
      if (filterConfig && activeFilters.length > 0) {
        for (const filter of activeFilters) {
          if (filter.values.length === 0) continue;

          switch (filter.type) {
            case 'tags':
              if (!filter.values.some(tag => card.tags?.includes(tag))) {
                return false;
              }
              break;
            case 'status':
              if (card.status && !filter.values.includes(card.status as string)) {
                return false;
              }
              break;
            case 'creator':
              if (!filter.values.includes(card.creator)) {
                return false;
              }
              break;
            case 'template':
              if ((card as any).blueprint_id && !filter.values.includes((card as any).blueprint_id as string)) {
                return false;
              }
              break;
            case 'version':
              if (card.version && !filter.values.includes(String(card.version))) {
                return false;
              }
              break;
          }
        }
      }

      return true;
    }).sort((a, b) => {
      // Sort by shadow mode grouping
      if (shadowMode === 'status' && a.status && b.status) {
        const statusCompare = (a.status || '').localeCompare(b.status || '');
        if (statusCompare !== 0) return statusCompare;
      } else if (shadowMode === 'template' && a.iconBg && b.iconBg) {
        const colorCompare = (a.iconBg || '').localeCompare(b.iconBg || '');
        if (colorCompare !== 0) return colorCompare;
      }
      
      // Sort alphabetically by title within groups
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    });
  }, [cards, searchTerm, requiredTags, selectedFolderId, filterConfig, activeFilters, shadowMode]);

  return {
    searchTerm,
    setSearchTerm,
    activeFilters,
    setActiveFilters,
    shadowMode,
    setShadowMode,
    filteredCards,
    allTags,
  };
}

